import lodash from "lodash-es";
import Bus from "../src/bus";
import { queryDealerShop, querySiteIdentityByShopId } from "../src/api/common";
import settings from "../src/settings";
import store from "../src/store";
import { storeData } from "../src/utils/localService";
import router, { baseRoutes } from "../src/router";
import { getToken, InitRoutesAndTreeMenuData, setToken } from "../src/utils/auth";
import { getQueryDealerShopBackUrl } from "../src/utils/devSite";
import { initNw } from "../src/utils/nw";
import { setMallAppToken } from "../src/utils/qiankun";
import { urlToSetCollectionH5SiteUrl } from "../src/utils/common";

let bootstrapPromise: Promise<void> | null = null;
let h5EventCenterBound = false;
let nwInitialized = false;

function updateFavicon(logoUrl?: string) {
  if (!logoUrl || !process.client) {
    return;
  }
  const link = document.querySelector("link.Home-link") as HTMLLinkElement | null;
  if (!link) {
    return;
  }
  link.rel = "icon";
  link.href = logoUrl;
  document.head.appendChild(link);
}

function applyUrlParamsToStorage() {
  if (!process.client) {
    return;
  }

  const url = new URL(window.location.href);
  const setJyToken = url.searchParams.get("setJyToken");
  const setMallToken = url.searchParams.get("setMallToken");
  const source = url.searchParams.get("source");
  const inviteCode = url.searchParams.get("inviteCode");
  const csPersonCode = url.searchParams.get("csPersonCode");
  const locale = url.searchParams.get("locale");

  if (setJyToken) {
    setToken(setJyToken);
  }
  if (setMallToken) {
    setMallAppToken(setMallToken);
  }
  if (source) {
    window.localStorage.setItem("source", source);
  }
  if (inviteCode) {
    window.localStorage.setItem("inviteCode", inviteCode);
  }
  if (csPersonCode) {
    window.localStorage.setItem("csPersonCode", csPersonCode);
  }
  if (locale) {
    window.localStorage.setItem("locale", locale);
    window.localStorage.setItem("now-locale", locale);
    window.localStorage.setItem("current_user_center_locale", locale);
  }

  if (window.location.href.indexOf("localhost") < 0) {
    urlToSetCollectionH5SiteUrl(window.location.href);
  }

  if (setJyToken || setMallToken) {
    url.searchParams.delete("setJyToken");
    url.searchParams.delete("setMallToken");
    window.history.replaceState({}, "", url.toString());
  }
}

async function ensureDealerShopData() {
  if (!process.client) {
    return;
  }

  const data = {
    shopBackUrl: getQueryDealerShopBackUrl(),
  };

  try {
    const response: any = await queryDealerShop(data);
    if (!response?.data?.title) {
      window.localStorage.removeItem("shopId");
      return;
    }

    store.commit("app/SET_STORE_INFO_DATA", response.data);
    if (settings.getCollectionH5SiteUrlByServiceList) {
      try {
        const siteList: any = await querySiteIdentityByShopId({ shopId: response.data.id });
        const h5Site = lodash.find(siteList?.data, (item: any) => item.siteType === 6);
        if (h5Site?.siteValue) {
          settings.collectionH5SiteUrl = h5Site.siteValue;
        }
      }
      catch (error) {
        console.error(error);
      }
    }

    const systemInfo = {
      ...response.data,
      title: `${response.data.title}`,
    };

    window.localStorage.setItem(storeData.StoreInformation, JSON.stringify(systemInfo));
    window.localStorage.setItem("shopId", response.data.id);
    document.title = systemInfo.title;
    updateFavicon(systemInfo.logoUrl);
  }
  catch (error) {
    console.error(error);
  }
}

function bindH5EventCenter() {
  if (!process.client || h5EventCenterBound) {
    return;
  }

  h5EventCenterBound = true;

  window.addEventListener("message", (event: MessageEvent) => {
    if (typeof event.data !== "string" || !event.data.startsWith("h5Message")) {
      return;
    }

    if (event.data.includes("forecast-back-packlist")) {
      router.push("/collection/package");
      return;
    }

    if (event.data.includes("back-wait-collection-list")) {
      Bus.$emit("declaration-refresh");
      router.push("/collection/package");
      return;
    }

    if (event.data.includes("h5Message-back-package-order-list")) {
      Bus.$emit("package-order-refresh");
      router.push("/collection/order");
      return;
    }

    if (event.data.includes("h5Message-reg-success")) {
      Bus.$emit("reg-success");
      return;
    }

    if (event.data.includes("h5Message-edit-package-detail-success")) {
      Bus.$emit("edit-package-detail-success");
      return;
    }

    if (event.data.includes("h5Message-collection-package-success")) {
      Bus.$emit("collection-package-success");
      return;
    }

    if (event.data.includes("h5Message-get-collection-package-data")) {
      Bus.$emit("get-collection-package-data");
      return;
    }

    if (event.data.includes("h5Message-create-package-order-success")) {
      Bus.$emit("create-package-order-success");
      return;
    }

    if (event.data.includes("h5Message-success-back-package-order-list")) {
      Bus.$emit("success-back-package-order-list");
      return;
    }

    if (event.data.includes("h5Message-refreshUserInfo-")) {
      store.dispatch("user/getInfo");
      store.dispatch("user/getFundInfo");
    }
  });
}

function ensureNwInitialized() {
  if (!process.client || nwInitialized) {
    return;
  }

  const maybeWindow = window as typeof window & {
    nw?: unknown;
    require?: (...args: any[]) => any;
  };

  if (!maybeWindow.nw || typeof maybeWindow.require !== "function") {
    return;
  }

  nwInitialized = true;
  initNw();
}

async function ensureRouteMenus() {
  if (!getToken()) {
    return;
  }

  await new Promise<void>((resolve) => {
    InitRoutesAndTreeMenuData(baseRoutes.children || [], () => resolve());
  });
}

export function ensureUserCenterBootstrapped() {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  bootstrapPromise = (async () => {
    ensureNwInitialized();
    applyUrlParamsToStorage();
    bindH5EventCenter();
    await ensureDealerShopData();
    await ensureRouteMenus();
  })();

  return bootstrapPromise;
}
